import React from 'react';
import { inject, observer } from 'mobx-react';
import type { AppStore } from '@stores/app/store';
import type { SiteStore } from '@stores/site/store';
import type { UserAction } from '@stores/user/action';
import type { ThreadAction } from '@stores/thread/action';
import PC from './layout/pc';
import H5 from './layout/h5';
import { BasePage } from '@components/Base/BasePage';
import styles from './index.module.scss';
import type { BasePageProps } from '../../types/base';
import { withRouter } from 'react-router-dom';
import { RouteComponentProps } from 'react-router-dom';
import type { BasePageState } from '../../types/base';
import { getCookie } from '@utils/cookie';
import getMessageTimestamp from '@utils/getMessageTimestamp';
import Toast from '@components/Toast';
import { uploadAttachment } from '../../server/api';
interface UserInfo {
    code: string;
    nickname?: string;
    avatar?: string;
    follow?: number;
    isFollowed?: boolean;
    [key: string]: any;  // 其他可能的属性
}

interface PrivateMessageProps extends BasePageProps, RouteComponentProps {
    app?: AppStore;
    site?: SiteStore;
    user?: UserAction;
    thread?: ThreadAction;
}
interface PrivateMessageState extends BasePageState {
    myValue: string;
    userInfo: UserInfo
    navList: any
    navCurrentIndex: any
    navCurrentType: number
    filterValue: any    // 筛选消息的类型
    unreadNotificationsData: any
    chatList: any    // 私信列表
    userChatList: any
    selectedRoomId: number
    chatScrollTrigger: number  // 递增触发 PC 聊天区滚动到底部
    chatInputContent: string   // 聊天输入框内容
    chatEmojiPickerVisible: boolean  // 表情选择器是否可见
    chatUploading: boolean     // 图片上传中
    chatSendLoading: boolean   // 消息发送中
    privateUserInfo: any       // 当前私信对象用户信息（通过 URL usercode 传入）
    usercode: string           // 路由上 usercode 参数
    // friendList: any // 关注/粉丝列表
    // friendType: any   // 类型（1是关注，为2是粉丝）
    // friendPage: any
}
@inject('app', 'site', 'user', 'thread')
@observer
class PrivateMessage extends BasePage<PrivateMessageProps, PrivateMessageState> {
    namespace = 'userNotificationsList';    // 列表命名空间
    currentLoadingCode: string = '';        // 用于记录当前正在加载的用户 code，防止竞态条件
    isLoadingMore: boolean = false;         // 防止重复加载更多
    pageNum: number = 1;                    // 聊天记录分页页码
    state = {
        loading: false,
        error: null,
        pageLoaded: false,
        myValue: '',
        userInfo: { code: '' },  // 提供默认值
        navList: [
            {name: '评论', id: 0, type: 0, filterValue: 'replied', num: 0 },
            {name: '@我', id: 1, type: 1, filterValue: 'related', num: 0},
            {name: '系统通知', id: 2, type: 2, filterValue: 'system,thread,post,trial_product,auction', num: 0},
            {name: '点赞和收藏', id: 3, type: 3, filterValue: 'liked,favorited', num: 0},
        ],  // tab切换列表
        filterValue: '',    // 筛选消息的类型
        navCurrentIndex: 0, // 导航当前选中索引
        navCurrentType: 0,  // 导航当前选中类型
        unreadNotificationsData: {},    // 未读消息数据
        chatList: [],    // 私信列表
        userChatList: [],   // 处理后的聊天记录列表（含时间分割）
        selectedRoomId: 0,  // 当前选中的聊天房间ID
        chatScrollTrigger: 0, // 聊天区滚动触发器
        chatInputContent: '', // 聊天输入框内容
        chatEmojiPickerVisible: false,  // 表情选择器是否可见
        chatUploading: false, // 图片上传中
        chatSendLoading: false, // 消息发送中
        privateUserInfo: null,  // 当前私信对象用户信息
        usercode: '',           // 路由上 usercode 参数
    };
    componentDidMount() {
        const { app } = this.props;
        // 从路由读取 roomId 和 usercode 参数
        const routerParams = new URLSearchParams(this.props.location.search);
        const roomId = Number(routerParams?.get('roomId')) || 0;
        const usercode = routerParams?.get('usercode') || '';

        if (app && app.platform === 'pc') {
            if (usercode) {
                // PC：如果 URL 有 usercode，不请求 chatList，直接以此为私信对象
                this.setState({ usercode, privateUserInfo: null });
                this.fetchPrivateUserInfo(usercode);
                if (roomId) {
                    this.setState({ selectedRoomId: roomId });
                    this.fetchChatMessageListData(1, roomId);
                }
            } else {
                // PC：加载私信列表后，默认选中第一个；若有 URL roomId 则使用该 roomId
                this.tryInitPCLayout(roomId);
            }
        } else if (app) {
            if (usercode) {
                // H5：如果 URL 有 usercode，直接以此为私信对象，获取用户信息
                this.setState({ usercode, privateUserInfo: null });
                this.fetchPrivateUserInfo(usercode);
                // 如果也有 roomId，加载聊天记录
                if (roomId) {
                    this.setState({ selectedRoomId: roomId });
                    this.fetchChatMessageListData(1, roomId);
                }
            } else if (roomId) {
                // H5：仅当 roomId 存在时加载聊天记录
                this.setState({ selectedRoomId: roomId });
                this.fetchChatMessageListData(1, roomId);
            }
            
        }
        this.getUnreadNotifications();
        super.componentDidMount();
    }

    // 记录上一次 user code，用于检测刷新后 userInfo 异步就绪（MobX 下 prevProps 无法对比）
    prevUserCode = '';

    // PC 初始化：加载私信列表第一页，根据 URL roomId 或列表第一项选中聊天室
    initPCLayout = async (urlRoomId: number) => {
        const { user } = this.props;
        if (!user) return;

        // 加载私信列表
        await user.fetchChatList({ page: 1, perPage: 20 });

        const flatList = user?.listStore?.getList({ namespace: 'chatList' }) || [];

        // 确定实际要选中的 roomId：
        // 1. URL 带 roomId 且在第一页列表中 → 使用该 roomId
        // 2. URL 带 roomId 但不在第一页 → 退回选第一个
        // 3. URL 无 roomId → 选第一个
        let targetRoomId = 0;
        if (urlRoomId && flatList.some((item: any) => item.roomId === urlRoomId)) {
            targetRoomId = urlRoomId;
        } else if (flatList.length > 0) {
            targetRoomId = flatList[0].roomId;
        }

        if (targetRoomId) {
            this.handleSelectChatRoom(targetRoomId);
        }
    }

    // 尝试初始化 PC 布局：user.store 可能在 mount 时尚未就绪（刷新场景），需等待
    tryInitPCLayout = (urlRoomId: number) => {
        const { app, user } = this.props;
        if (!app || !user) return;

        // 若 userInfo 尚未就绪，等 componentDidUpdate 触发
        if (!user.userInfo?.code) return;

        // 如果 URL 上有 usercode，跳过 chatList 请求
        if (this.state.usercode) return;

        this.initPCLayout(urlRoomId);
    }

    componentDidUpdate(prevProps: PrivateMessageProps, prevState: PrivateMessageState) {
        const { app, user } = this.props;
        if (!app || app.platform !== 'pc' || !user) return;

        const currentCode = user?.userInfo?.code || '';

        // 刷新页面后 userInfo 异步加载完成（code 从空变有值）→ 触发初始化
        if (!this.prevUserCode && currentCode) {
            const roomId = this.getRoomId();
            this.tryInitPCLayout(roomId);
        }

        this.prevUserCode = currentCode;
    }
    componentWillUnmount(): void {
    }

    // 读取带未读的会话后，同步 header 铃铛和私信未读总数
    refreshUnreadCountsAfterChatRead = async () => {
        const { user } = this.props;
        if (!user?.userInfo?.code) return;

        await user.fetchUserInfo({ code: user.userInfo.code });
        await this.getUnreadNotifications();
    }

    // 获取和某人的私信聊天记录
    fetchChatMessageListData = async (page?: number, roomId?: number) => {
        const { app, user } = this.props;
        // 优先使用传入的 roomId，否则从路由读取
        const resolvedRoomId = roomId !== undefined ? roomId : (() => {
            const routerParams = new URLSearchParams(this.props.location.search);
            return Number(routerParams?.get('roomId')) || 0;
        })();
        if (!resolvedRoomId || !user) return;

        const pageNum = page || 1;
        this.pageNum = pageNum;

        const params = {
            'filter[roomId]': resolvedRoomId,
            page: pageNum,
            perPage: 20
        };

        // 请求前记录该会话是否有未读（加载聊天记录后服务端会标记已读）
        const flatList = user?.listStore?.getList({ namespace: 'chatList' }) || [];
        const currentChat = flatList.find((item: any) => item.roomId === resolvedRoomId);
        const hadUnread = pageNum <= 1 && (currentChat?.unreadCount || 0) > 0;

        const res = await user.fetchChatMessageList(params);
        if (res.code !== 0) return;

        const pageData = res.data?.pageData || res.data?.data || [];
        if (pageData.length === 0) return;

        let { userChatList } = this.state;

        if (pageNum <= 1) {
            // 第一页：反转数据后 push（最新消息在底部）
            const reversed = [...pageData].reverse();
            for (let i = 0; i < reversed.length; i++) {
                reversed[i] = { ...reversed[i], timestamp: reversed[i].createdAt };
            }
            userChatList = [...reversed];

            // 加载了聊天记录，将对应私信列表项的未读数清零
            user?.listStore?.updateListItemByKey({
                namespace: 'chatList',
                matchKey: 'roomId',
                matchValue: resolvedRoomId,
                updates: { unreadCount: 0 },
            });

            // 首次加载/切换房间：触发滚动到底部显示最新消息
            this.setState(prev => ({ chatScrollTrigger: prev.chatScrollTrigger + 1 }));

            // 该会话原有未读时，刷新全局未读数（header 铃铛、私信总数）
            if (hadUnread && app?.platform === 'pc') {
                this.refreshUnreadCountsAfterChatRead();
            }
        } else {
            // 第二页及之后：unshift 到前面（更早的消息在顶部）
            const newItems = pageData.map((item: any) => ({
                ...item,
                timestamp: item.createdAt,
            }));
            userChatList = [...newItems, ...userChatList];
        }

        // 插入时间分割线
        userChatList = getMessageTimestamp.getMessageTimestamp(userChatList);

        this.setState({ userChatList });
    }

    // 点击私信列表项，加载对应聊天记录
    handleSelectChatRoom = (roomId: number) => {
        // 更新 URL 参数
        const searchParams = new URLSearchParams(this.props.location.search);
        searchParams.set('roomId', String(roomId));
        this.props.history?.replace({
            pathname: this.props.location.pathname,
            search: searchParams.toString(),
        });
        this.setState({ selectedRoomId: roomId });
        // 重置页码并加载数据
        this.fetchChatMessageListData(1, roomId);
    }

    // 获取 roomId
    getRoomId = () => {
        const routerParams = new URLSearchParams(this.props.location.search);
        return Number(routerParams?.get('roomId')) || 0;
    }

    // 获取未读消息数
    getUnreadNotifications = async () => {
        const { app, user } = this.props;
        const { navList } = this.state;
        if(user) {
            const res = await user.getUnreadNotifications();
            console.log(res, '未读消息数');
            this.setState({
                unreadNotificationsData: res.data
            }, () => {
                // let navListCopy = navList;
                
                // // const relatedAndrepliedUnreadNum = (res.data?.typeUnreadNotifications?.replied + res.data?.typeUnreadNotifications?.related) || 0;  // 评论和@未读消息数
                // const systemUnreadNum = (res.data?.typeUnreadNotifications?.system + res.data?.typeUnreadNotifications?.thread + res.data?.typeUnreadNotifications?.post + res.data?.typeUnreadNotifications?.trial_product + res.data?.typeUnreadNotifications?.auction) || 0;  // 系统通知未读消息数
                // const likeAndFavoriteUnreadNum = (res.data?.typeUnreadNotifications?.liked + res.data?.typeUnreadNotifications?.favorited) || 0;  // 点赞和收藏未读消息数
                // navListCopy[0].num = res.data?.typeUnreadNotifications?.replied || 0;
                // navListCopy[1].num = res.data?.typeUnreadNotifications?.related || 0;
                // navListCopy[2].num = systemUnreadNum;
                // navListCopy[3].num = likeAndFavoriteUnreadNum;
                // this.setState({
                //     navList: navListCopy
                // })

            })
        }
    }

    // 一键已读：清空所有未读消息
    submitNotificationsReadData = async () => {
        const { user } = this.props;
        if (!user) return;

        const res = await user.markAllNotificationsAsRead();
        if (res.code === 0) {
            this.getUnreadNotifications();
            Toast.info('清除消息成功');
        }
    }
    // 分页
    handleLoadMore = () => {
        const { user } = this.props;
        if (this.isLoadingMore) return;

        const namespace = 'userChatMessageList';
        const list = user?.listStore?.lists?.[namespace];
        if (!list || !list.hasMore) return;

        this.isLoadingMore = true;
        const nextPage = this.pageNum + 1;

        this.fetchChatMessageListData(nextPage)
            .finally(() => {
                this.isLoadingMore = false;
            });
    }

    // 聊天输入内容变化
    handleChatInputChange = (content: string) => {
        this.setState({ chatInputContent: content });
    }

    // 切换表情选择器
    handleChatEmojiToggle = () => {
        this.setState(prev => ({ chatEmojiPickerVisible: !prev.chatEmojiPickerVisible }));
    }

    // 关闭表情选择器
    handleChatEmojiClose = () => {
        this.setState({ chatEmojiPickerVisible: false });
    }

    // 处理文件选择 → 上传图片 → 直接发送图片消息
    handleChatFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const { user } = this.props;
        const { selectedRoomId } = this.state;
        if (!user) return;

        const receiverCode = this.getReceiverCode(user);
        if (!receiverCode) return;

        this.setState({ chatUploading: true });
        try {
            const file = files[0];
            const formData = new FormData();
            formData.append('type', '4');
            formData.append('file', file);

            const uploadRes = await uploadAttachment(formData);
            if (!(uploadRes && uploadRes.code === 0 && uploadRes.data)) {
                Toast.info('图片上传失败');
                return;
            }

            // 上传成功，直接发送图片消息
            const sendRes = await user.submitChatMessageSendData({
                receiverCode,
                type: 2,
                content: uploadRes.data.id,
            });

            if (sendRes.code === 0) {
                // 发送成功后触发滚动到底部 & 刷新聊天记录
                this.setState(prev => ({ chatScrollTrigger: prev.chatScrollTrigger + 1 }));
                if (selectedRoomId) {
                    this.fetchChatMessageListData(1, selectedRoomId);
                }
            } else {
                Toast.info('发送图片失败');
            }
        } catch (error) {
            console.error('上传发送错误:', error);
            Toast.info('发送图片出错');
        } finally {
            this.setState({ chatUploading: false });
        }
    }

    // 获取 URL 上 usercode 对应的用户信息
    fetchPrivateUserInfo = (usercode: string) => {
        const { user } = this.props;
        if (!user || !usercode) return;
        user.getUserInfo({ code: usercode }).then(res => {
            if (res.code === 0) {
                this.setState({ privateUserInfo: res.data });
            }
        });
    }

    // 获取当前聊天对象的 receiverCode
    // 优先级：URL usercode > chatList > userChatList 推断
    getReceiverCode = (user: UserAction) => {
        const { selectedRoomId, userChatList, usercode } = this.state;
        // 优先使用 URL 上的 usercode
        if (usercode) return usercode;
        // 其次从私信列表中获取
        const flatList = user?.listStore?.getList({ namespace: 'chatList' }) || [];
        const currentChat = flatList.find((item: any) => item.roomId === selectedRoomId);
        let receiverCode = currentChat?.receiver?.code || '';
        // H5 fallback：从聊天记录中推断对方 code
        if (!receiverCode && userChatList && userChatList.length > 0) {
            const myCode = user?.userInfo?.code;
            const firstMsg = userChatList[0];
            if (firstMsg.sender?.code === myCode) {
                receiverCode = firstMsg.receiver?.code || '';
            } else {
                receiverCode = firstMsg.sender?.code || '';
            }
        }
        return receiverCode;
    }

    // 发送文本私信
    submitChatMessageSendData = async () => {
        const { user } = this.props;
        const { chatInputContent, selectedRoomId } = this.state;
        if (!user || !selectedRoomId) return;

        if (!chatInputContent.trim()) {
            Toast.info('请输入内容');
            return;
        }

        // 从私信列表中获取当前聊天对象的 receiverCode
        const receiverCode = this.getReceiverCode(user);

        this.setState({ chatSendLoading: true });
        
        try {
            const res = await user.submitChatMessageSendData({
                receiverCode,
                type: 1,
                content: chatInputContent,
            });
            if (res.code === 0) {
                this.setState(prev => ({ chatInputContent: '', chatEmojiPickerVisible: false, chatScrollTrigger: prev.chatScrollTrigger + 1 }));
            } else {
                Toast.info(`${res.msg}`);
            }

            // 发送成功后刷新聊天记录
            this.fetchChatMessageListData(1, selectedRoomId);
        } catch (error) {
            console.error('发送私信错误:', error);
        } finally {
            this.setState({ chatSendLoading: false });
        }
    }



    render() {
        const { app, user } = this.props;
        let { userInfo, navList, navCurrentIndex, navCurrentType, unreadNotificationsData, userChatList, selectedRoomId, chatScrollTrigger, chatInputContent, chatEmojiPickerVisible, chatUploading, chatSendLoading, privateUserInfo } = this.state;
        const platform = app?.platform || 'h5';
        // 【重要】在 render 中读取 user.userInfo.code，确保 MobX @observer 追踪其变化，触发 componentDidUpdate
        void (user?.userInfo?.code as any);
        // 如果平台信息还未初始化，显示加载状态
        if (!app?.platform) {
            return (
                <div className={styles.loading} style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh'
                }}>
                    <div>加载中...</div>
                </div>
            );
        } else {
            return (<div className={platform === 'pc' ? styles.pcWrap : styles.h5Wrap}>
                {platform === 'pc' ? (<PC
                    userInfo={userInfo}
                    userChatList={userChatList}
                    privateUserInfo={privateUserInfo}
                    handleLoadMore={this.handleLoadMore}
                    unreadNotificationsData={unreadNotificationsData}
                    onSelectChatRoom={this.handleSelectChatRoom}
                    submitNotificationsReadData={this.submitNotificationsReadData}
                    selectedRoomId={selectedRoomId}
                    chatScrollTrigger={chatScrollTrigger}
                    chatInputContent={chatInputContent}
                    chatEmojiPickerVisible={chatEmojiPickerVisible}
                    chatUploading={chatUploading}
                    chatSendLoading={chatSendLoading}
                    onChatInputChange={this.handleChatInputChange}
                    onChatEmojiToggle={this.handleChatEmojiToggle}
                    onChatEmojiClose={this.handleChatEmojiClose}
                    onChatFileUpload={this.handleChatFileChange}
                    onChatSend={this.submitChatMessageSendData}
                ></PC>) : (
                    <H5
                        userInfo={userInfo}
                        userChatList={userChatList}
                        privateUserInfo={privateUserInfo}
                        handleLoadMore={this.handleLoadMore}
                        unreadNotificationsData={unreadNotificationsData}
                        submitNotificationsReadData={this.submitNotificationsReadData}
                        chatScrollTrigger={chatScrollTrigger}
                        chatInputContent={chatInputContent}
                        chatEmojiPickerVisible={chatEmojiPickerVisible}
                        chatUploading={chatUploading}
                        chatSendLoading={chatSendLoading}
                        onChatInputChange={this.handleChatInputChange}
                        onChatEmojiToggle={this.handleChatEmojiToggle}
                        onChatEmojiClose={this.handleChatEmojiClose}
                        onChatFileUpload={this.handleChatFileChange}
                        onChatSend={this.submitChatMessageSendData}
                    ></H5>
                )}
            </div>)
        }

    }
}

export default withRouter(PrivateMessage);
